import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { LogOut, Inbox, ScanLine, FileText, Layers, Trash2, Users, Settings as SettingsIcon, StickyNote, Mail, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import Logo from "../../components/Logo";
import AdminProjects from "./AdminProjects";
import AdminInbox from "./AdminInbox";
import AdminTeam from "./AdminTeam";

const STATUS = ["new", "contacted", "quoted", "won", "closed"];

const sydFmt = new Intl.DateTimeFormat("en-AU", {
  timeZone: "Australia/Sydney", weekday: "short", day: "2-digit",
  month: "short", hour: "2-digit", minute: "2-digit", hour12: false,
});
const syd = (iso) => { try { return sydFmt.format(new Date(iso)); } catch { return "—"; } };

const StatCard = ({ icon: Icon, label, value }) => (
  <div className="rounded-lg border border-white/10 bg-obsidian-800 p-6">
    <Icon className="text-neon mb-3" size={22} />
    <p className="font-heading font-black text-3xl">{value ?? "—"}</p>
    <p className="font-mono text-[11px] uppercase tracking-widest text-zinc-500 mt-1">{label}</p>
  </div>
);

export default function AdminDashboard() {
  const { user, isOwner, logout } = useAuth();
  const nav = useNavigate();
  const [tab, setTab] = useState(() => new URLSearchParams(window.location.search).get("tab") || "leads");
  const [stats, setStats] = useState({});
  const [leads, setLeads] = useState([]);
  const [founders, setFounders] = useState([]);
  const [settings, setSettings] = useState(null);
  const [selected, setSelected] = useState(null);
  const [notes, setNotes] = useState("");
  const [savingNotes, setSavingNotes] = useState(false);

  useEffect(() => { setNotes(selected?.notes || ""); }, [selected?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const load = () => {
    api.get("/admin/stats").then((r) => setStats(r.data)).catch(() => {});
    api.get("/admin/leads").then((r) => setLeads(r.data)).catch(() => {});
    api.get("/founders").then((r) => setFounders(r.data)).catch(() => {});
    api.get("/settings").then((r) => setSettings(r.data)).catch(() => {});
  };
  useEffect(load, []);

  const updateStatus = async (id, status) => {
    await api.patch(`/admin/leads/${id}`, { status });
    setLeads((ls) => ls.map((l) => (l.id === id ? { ...l, status } : l)));
    if (selected?.id === id) setSelected((s) => ({ ...s, status }));
  };
  const del = async (id) => {
    if (!window.confirm("Delete this lead?")) return;
    await api.delete(`/admin/leads/${id}`);
    setLeads((ls) => ls.filter((l) => l.id !== id));
    setSelected(null);
    toast.success("Lead deleted");
  };
  const saveNotes = async () => {
    setSavingNotes(true);
    try {
      await api.patch(`/admin/leads/${selected.id}`, { notes });
      setLeads((ls) => ls.map((l) => (l.id === selected.id ? { ...l, notes } : l)));
      setSelected((s) => ({ ...s, notes }));
      toast.success("Notes saved");
    } catch {
      toast.error("Could not save notes");
    } finally {
      setSavingNotes(false);
    }
  };

  const saveFounder = async (fdr) => {
    await api.patch(`/admin/founders/${fdr.id}`, fdr);
    toast.success("Team member saved");
  };
  const uploadPhoto = async (fdr, file) => {
    if (!file) return;
    const form = new FormData();
    form.append("file", file);
    try {
      const r = await api.post("/admin/upload", form, { headers: { "Content-Type": "multipart/form-data" } });
      const updated = await api.patch(`/admin/founders/${fdr.id}`, { image: r.data.url });
      setFounders((fs) => fs.map((f) => (f.id === fdr.id ? updated.data : f)));
      toast.success("Photo uploaded");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
    }
  };
  const saveSettings = async () => {
    await api.put("/admin/settings", settings);
    toast.success("Settings saved");
  };

  const doLogout = () => { logout(); nav("/admin/login"); };

  return (
    <div className="min-h-screen bg-obsidian text-white">
      <header className="glass sticky top-0 z-30 flex items-center justify-between px-5 md:px-8 py-4 border-b border-white/10">
        <div className="flex items-center gap-6">
          <Logo compact />
          <span className="hidden md:block font-mono text-[11px] uppercase tracking-widest text-zinc-500">CMS Dashboard</span>
        </div>
        <div className="flex items-center gap-4">
          <span className="hidden md:flex items-center gap-2 text-sm text-zinc-400">
            {user?.email}
            <span className={`rounded-sm px-1.5 py-0.5 font-mono text-[9px] uppercase tracking-widest ${isOwner ? "bg-gold/15 text-gold" : "bg-white/10 text-zinc-300"}`} data-testid="admin-role-badge">{user?.role}</span>
          </span>
          <button onClick={doLogout} data-testid="admin-logout" className="inline-flex items-center gap-2 rounded-sm border border-white/15 px-4 py-2 text-sm hover:border-neon hover:text-neon transition-colors"><LogOut size={15} /> Logout</button>
        </div>
      </header>

      <div className="mx-auto max-w-7xl px-5 md:px-8 py-8">
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-8">
          <StatCard icon={Inbox} label="Total Leads" value={stats.leads} />
          <StatCard icon={ScanLine} label="Court Scans" value={stats.court_scans} />
          <StatCard icon={FileText} label="Quote Requests" value={stats.quotes} />
          <StatCard icon={Inbox} label="New" value={stats.new_leads} />
          <StatCard icon={Layers} label="Projects" value={stats.projects} />
        </div>

        <div className="flex gap-2 mb-6 border-b border-white/10 overflow-x-auto whitespace-nowrap">
          {[["leads", "Leads", Inbox], ["inbox", "Email Inbox", Mail], ["projects", "Projects", Layers], ["founders", "Team Speedy", Users], ["settings", "Settings", SettingsIcon], ...(isOwner ? [["team", "Admin Access", ShieldCheck]] : [])].map(([k, label, Icon]) => (
            <button key={k} onClick={() => setTab(k)} data-testid={`tab-${k}`}
              className={`inline-flex shrink-0 items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 -mb-px transition-colors ${tab === k ? "border-neon text-neon" : "border-transparent text-zinc-400 hover:text-white"}`}>
              <Icon size={16} /> {label}
            </button>
          ))}
        </div>

        {tab === "leads" && (
          <div className="grid lg:grid-cols-2 gap-5">
            <div className="rounded-lg border border-white/10 bg-obsidian-800 overflow-hidden" data-testid="leads-list">
              {leads.length === 0 && <p className="p-8 text-center text-sm text-zinc-500 font-mono">No leads yet.</p>}
              {leads.map((l) => (
                <button key={l.id} onClick={() => setSelected(l)} data-testid={`lead-${l.id}`}
                  className={`w-full text-left flex items-center justify-between gap-3 px-5 py-4 border-b border-white/5 hover:bg-white/[0.03] transition-colors ${selected?.id === l.id ? "bg-white/[0.04]" : ""}`}>
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <span className={`font-mono text-[9px] uppercase px-1.5 py-0.5 rounded-sm ${l.type === "court_scan" ? "bg-neon/15 text-neon" : l.type === "customiser" ? "bg-white/10 text-zinc-300" : l.type === "callback" ? "bg-rose-400/15 text-rose-300" : "bg-gold/15 text-gold"}`}>{{ court_scan: "SCAN", quote: "QUOTE", customiser: "DESIGN", callback: "CALL BACK" }[l.type] || l.type}</span>
                      <span className="font-medium truncate">{l.contact_name || l.email || l.phone || "Anonymous"}</span>
                      {l.notes && <StickyNote size={12} className="text-gold shrink-0" />}
                    </div>
                    <p className="text-xs text-zinc-500 mt-0.5 truncate">{l.court_type} · {l.postcode || "—"}</p>
                    <p className="font-mono text-[10px] text-zinc-400 mt-0.5" data-testid={`lead-time-${l.id}`}>{syd(l.created_at)} · Sydney</p>
                  </div>
                  <span className="font-mono text-[10px] uppercase text-zinc-500 shrink-0">{l.status}</span>
                </button>
              ))}
            </div>

            <div className="rounded-lg border border-white/10 bg-obsidian-800 p-6" data-testid="lead-detail">
              {!selected ? <p className="text-sm text-zinc-500 font-mono text-center py-12">Select a lead to view details.</p> : (
                <div>
                  <div className="flex items-start justify-between">
                    <h3 className="font-heading font-bold text-xl">{selected.contact_name || "Anonymous"}</h3>
                    <button onClick={() => del(selected.id)} data-testid="lead-delete" className="text-zinc-500 hover:text-red-400"><Trash2 size={18} /></button>
                  </div>
                  <div className="mt-4 space-y-2 text-sm">
                    <Row k="Received (Sydney)" v={syd(selected.created_at)} />
                    <Row k="Type" v={selected.type} />
                    <Row k="Email" v={selected.email} />
                    <Row k="Phone" v={selected.phone} />
                    <Row k="Court" v={selected.court_type} />
                    <Row k="Postcode" v={selected.postcode} />
                    {Object.entries(selected.data || {}).map(([k, v]) => <Row key={k} k={k} v={typeof v === "object" ? JSON.stringify(v) : String(v)} />)}
                  </div>
                  <div className="mt-5">
                    <p className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 mb-2">Status</p>
                    <div className="flex flex-wrap gap-2">
                      {STATUS.map((s) => (
                        <button key={s} onClick={() => updateStatus(selected.id, s)} data-testid={`status-${s}`}
                          className={`rounded-sm border px-3 py-1.5 text-xs capitalize ${selected.status === s ? "border-neon text-neon" : "border-white/10 text-zinc-400 hover:border-white/30"}`}>{s}</button>
                      ))}
                    </div>
                  </div>
                  <div className="mt-5">
                    <p className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 mb-2">Follow-Up Notes</p>
                    <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={4} data-testid="lead-notes-input"
                      placeholder="Call outcomes, quote details, follow-up reminders…"
                      className="w-full bg-obsidian-700 border border-white/10 rounded-sm p-3 text-sm outline-none focus:border-neon" />
                    <button onClick={saveNotes} disabled={savingNotes} data-testid="lead-notes-save"
                      className="mt-2 rounded-sm bg-neon px-5 py-2.5 text-sm font-bold text-obsidian hover:bg-neon-hover transition-colors disabled:opacity-60">
                      {savingNotes ? "Saving…" : "Save Notes"}
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {tab === "inbox" && <AdminInbox />}

        {tab === "team" && isOwner && <AdminTeam />}

        {tab === "projects" && <AdminProjects onChange={() => api.get("/admin/stats").then((r) => setStats(r.data)).catch(() => {})} />}

        {tab === "founders" && (
          <div className="grid md:grid-cols-2 gap-5">
            {founders.map((fdr, idx) => (
              <div key={fdr.id} className="rounded-lg border border-white/10 bg-obsidian-800 p-6" data-testid={`founder-edit-${idx}`}>
                <p className="font-mono text-[10px] uppercase tracking-widest text-gold mb-4">{fdr.country}</p>
                <div className="mb-4 flex items-center gap-4">
                  <div className="h-20 w-20 rounded-full overflow-hidden border border-gold/30 bg-obsidian-700 flex items-center justify-center shrink-0">
                    {fdr.image ? <img src={fdr.image} alt={fdr.name} className="h-full w-full object-cover" />
                      : <Users size={26} className="text-zinc-600" />}
                  </div>
                  <label className="cursor-pointer rounded-sm border border-neon/40 px-4 py-2.5 text-sm font-bold text-neon hover:bg-neon hover:text-obsidian transition-colors">
                    Upload Photo
                    <input type="file" accept="image/*" className="hidden" data-testid={`upload-photo-${idx}`}
                      onChange={(e) => { uploadPhoto(fdr, e.target.files?.[0]); e.target.value = ""; }} />
                  </label>
                </div>
                {["name", "role", "specialisation", "contribution"].map((k) => (
                  <input key={k} defaultValue={fdr[k]} placeholder={k} onChange={(e) => (fdr[k] = e.target.value)}
                    className="mb-3 w-full bg-obsidian-700 border border-white/10 rounded-sm p-3 text-sm outline-none focus:border-neon" />
                ))}
                <textarea defaultValue={fdr.story} placeholder="story" rows={3} onChange={(e) => (fdr.story = e.target.value)}
                  className="mb-3 w-full bg-obsidian-700 border border-white/10 rounded-sm p-3 text-sm outline-none focus:border-neon" />
                <input defaultValue={fdr.image} placeholder="or paste a photo URL" onChange={(e) => (fdr.image = e.target.value)}
                  className="mb-4 w-full bg-obsidian-700 border border-white/10 rounded-sm p-3 text-sm outline-none focus:border-neon" />
                <button onClick={() => saveFounder(fdr)} data-testid={`save-founder-${idx}`} className="rounded-sm bg-neon px-5 py-2.5 text-sm font-bold text-obsidian">Save Team Member</button>
              </div>
            ))}
          </div>
        )}

        {tab === "settings" && settings && (
          <div className="rounded-lg border border-white/10 bg-obsidian-800 p-6 max-w-2xl" data-testid="settings-form">
            {[["phone", "Phone"], ["email", "Email (all leads are sent here)"], ["address", "Address"], ["instagram", "Instagram URL"], ["facebook", "Facebook URL"], ["tiktok", "TikTok URL"], ["linkedin", "LinkedIn URL"], ["youtube", "YouTube URL"], ["google_business", "Google Business URL"], ["parent_url", "Parent site URL"], ["timezone", "Timezone"], ["cm3_id", "Cm3 ID (shown on /compliance)"], ["cm3_expiry", "Cm3 expiry (YYYY-MM-DD)"], ["cm3_verify_url", "Cm3 verification URL"], ["public_liability", "Public liability cover"]].map(([k, label]) => (
              <div key={k} className="mb-4">
                <label className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">{label}</label>
                <input value={settings[k] || ""} onChange={(e) => setSettings((s) => ({ ...s, [k]: e.target.value }))} data-testid={`setting-${k}`}
                  className="mt-1.5 w-full bg-obsidian-700 border border-white/10 rounded-sm p-3 text-sm outline-none focus:border-neon" />
              </div>
            ))}
            <button onClick={saveSettings} data-testid="save-settings" className="rounded-sm bg-neon px-6 py-3 text-sm font-bold text-obsidian">Save Settings</button>
          </div>
        )}
      </div>
    </div>
  );
}

const Row = ({ k, v }) => (
  <div className="flex justify-between gap-4 border-b border-white/5 pb-1.5">
    <span className="font-mono text-[11px] uppercase text-zinc-500 capitalize">{k.replace(/_/g, " ")}</span>
    <span className="text-zinc-200 text-right break-all">{v || "—"}</span>
  </div>
);
